import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import type {SidebarPinnedView} from '@github-ui/repos-issues-sidebar-ui/PinnedViewsNav'
import type {User} from '@github-ui/use-user'

import {issuesAppBuilder} from '../config/app-builder'

export type IssuesLayoutPayload = {
  has_projects_enabled: boolean
  has_labels_archiving_enabled: boolean
  // The DataRouter app ships no app payload, so the viewer arrives here instead.
  // Null for logged-out viewers, undefined against a dotcom without github/github#444471.
  current_user?: User | null
  current_user_settings?: {
    preferred_emoji_skin_tone?: number | null
    copilot_show_functionality: boolean
  }
  // Server-read seed of the sidebar collapse cookie; null when disabled. Passed
  // as `initialExpanded` so a collapsed sidebar doesn't flash open on SSR.
  sidebar_expanded?: boolean | null
  // Server-evaluated pinned saved views (empty/absent when the feature is off) so
  // data-router leaves render them on first paint without a client flag or fetch.
  pinned_views?: SidebarPinnedView[]
  // Index page config mirrored from the Relay index payload; present on the index path.
  best_match_sort_enabled?: boolean
  issue_types_enabled?: boolean
  issue_search_type?: string | null
  show_issue_field_pills?: boolean
  viewer_can_create_issues?: boolean
  is_writable?: boolean
}

/**
 * Issues layout route configuration.
 *
 * Defined as a builder-parameterized factory so the same route wiring can be registered
 * under multiple apps (e.g. `issues` and `repo`). Route configs are bound to the app
 * builder that creates them (embedded-data hydration + query-key app name), so each app
 * must build its own config rather than share a single instance.
 */
export function buildIssuesLayoutRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('issuesLayoutRoute', {
    path: '/:owner/:repo',
    queries: [
      mainQuery<IssuesLayoutPayload>({
        // fetches shared layout data (e.g. sidebar config) from a dedicated endpoint,
        // cached and reused across all child routes (e.g. labels, milestones...)
        queryDeps: ({pathname}) => {
          // comma in the beginning to omit the empty string result of split before the first slash
          const [, owner, repo] = pathname.split('/')
          return {pathname: `/${owner}/${repo}/issues/_layout`}
        },
        staleTimeForNavigation: 360000,
      }),
    ],
  })
}

/**
 * Route config bound to the `issues` app builder, imported by components that read this
 * route's query data and params with static typing.
 */
export const issuesLayoutRoute = buildIssuesLayoutRoute(issuesAppBuilder)
